import { Button, Tooltip } from '@getro/rombo';
import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Flex, Text } from 'rebass/styled-components';
import PropTypes from 'prop-types';

import isEqual from 'lodash.isequal';
import { ScrollBox } from 'components/atoms/ScrollBox';
import { SidebarClose } from 'lucide-react';
import { activeNetworkSelector } from '../../../redux/selectors';
import { reverseParsedFilters, trackContactFiltersEvent } from '../../../services/contacts/filters';
import { Filters } from './filters';
import { FiltersModal } from './filtersModal';
import { companyDefaultFilters } from './defaultFilters';

export const CompanyFilters = memo(
  ({
    filters,
    filterCount,
    setFilters,
    extendedMode,
    trackingLocation,
    sharedList,
    showListFilters,
    filtersTitle,
    setFiltersVisible,
    saveFilters,
    savedFilters,
    isSavingFilters,
  }) => {
    const activeNetwork = useSelector(activeNetworkSelector);
    const setMultipleFilters = (group, options = {}) => {
      const { removeFilters = [] } = options;
      const cleanFilters = Object.keys(group).reduce((arr, key) => {
        if (group[key]?.value === 'any') {
          removeFilters.push(key);
          return arr;
        }

        arr[key] = group[key]; // eslint-disable-line no-param-reassign
        return arr;
      }, {});
      const newFilters = { ...filters, ...cleanFilters };

      [...new Set(removeFilters)].forEach((item) => {
        delete newFilters[item];
      });

      setFilters(newFilters);

      trackContactFiltersEvent({
        group,
        activeNetwork,
        trackingLocation,
        sharedList,
        newFilters,
      });
    };

    const onResetFilters = () => {
      setFilters(companyDefaultFilters);
    };

    const disableSaveFilters = useMemo(() => {
      const formattedFilters = { ...companyDefaultFilters, ...reverseParsedFilters(savedFilters, ['companyIds']) };

      return isEqual(filters, formattedFilters);
    }, [filters, savedFilters]);

    return (
      <>
        <ScrollBox
          data-testid="company-filters"
          px={[0, 0, '16px']}
          minWidth="264px"
          width="264px"
          flex="0 0 auto"
          display={['none', 'none', 'block']}
          sx={{
            mb: ['40px', '40px', 0],
            position: ['relative', 'relative', 'sticky'],
            top: 0,
            height: ['auto', 'auto', 'calc(100vh - 80px)'],
            overflowY: ['visible', 'visible', 'auto'],
            borderRight: '1px solid #E0E0E0',
          }}
        >
          {!!filtersTitle && (
            <Box
              display={['none', 'none', 'flex']}
              pt="8px"
              pb="8px"
              sx={{
                color: 'text.subtle',
                fontSize: '12px',
                textTransform: 'uppercase',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Text
                data-testid="contact-filters-title"
                sx={{
                  textTransform: 'uppercase',
                  color: 'text.subtle',
                  fontSize: '12px',
                  letterSpacing: '1px',
                }}
              >
                {filtersTitle}
              </Text>

              <Flex
                alignItems="center"
                justifyContent="center"
                width="24px"
                height="24px"
                sx={{ cursor: 'pointer' }}
                onClick={() => setFiltersVisible(false)}
              >
                <Box
                  as={SidebarClose}
                  className="main-menu-item"
                  width="16px"
                  height="16px"
                  aria-hidden="true"
                  flex="0 0 auto"
                />
              </Flex>
            </Box>
          )}
          <Box>
            <Filters
              sharedList={sharedList}
              filters={filters}
              filterCount={filterCount}
              setFilters={setMultipleFilters}
              extendedMode={extendedMode}
              isTalentNetworkEnabled={!activeNetwork?.tnHidden}
              showListFilters={showListFilters}
            />
          </Box>
          <Flex
            alignItems="center"
            bg="neutral.0"
            py="16px"
            sx={{ gap: '8px', position: 'absolute', bottom: 0, left: '16px', right: '16px' }}
          >
            <Tooltip
              position="top-end"
              sx={{
                '.tooltip-arrow': {
                  transform: 'translate(10px, 0px) !important',
                },
              }}
              content={
                <Box fontSize="14px" color="neutral.0">
                  {disableSaveFilters
                    ? 'No changes to save'
                    : 'Save selected filters to keep them active, including for the shared view.'}
                </Box>
              }
              trigger={
                <Button
                  sx={{ cursor: disableSaveFilters ? 'not-allowed' : 'pointer' }}
                  disabled={disableSaveFilters}
                  size="small"
                  onClick={() => saveFilters(filters)}
                  loading={isSavingFilters}
                >
                  Save filters
                </Button>
              }
            />
            {filterCount > 0 && (
              <Button variant="tertiary" size="small" onClick={onResetFilters}>
                Clear all
              </Button>
            )}
          </Flex>
        </ScrollBox>
        <FiltersModal
          sharedList={sharedList}
          filters={filters}
          filterCount={filterCount}
          setFilters={setMultipleFilters}
          saveFilters={saveFilters}
          extendedMode={extendedMode}
          isTalentNetworkEnabled={!activeNetwork?.tnHidden}
          onClose={() => setFiltersVisible(false)}
          resetFilters={onResetFilters}
          showListFilters={showListFilters}
          disableSaveFilters={disableSaveFilters}
          isSavingFilters={isSavingFilters}
        />
      </>
    );
  },
);

CompanyFilters.propTypes = {
  sharedList: PropTypes.object,
  extendedMode: PropTypes.bool,
  filters: PropTypes.object.isRequired,
  filterCount: PropTypes.number,
  setFilters: PropTypes.func.isRequired,
  trackingLocation: PropTypes.oneOf(['admin_portal', 'shared_list']),
  showListFilters: PropTypes.bool,
  filtersTitle: PropTypes.string,
  setFiltersVisible: PropTypes.func.isRequired,
  saveFilters: PropTypes.func,
  savedFilters: PropTypes.object,
  isSavingFilters: PropTypes.bool,
};

CompanyFilters.defaultProps = {
  filterCount: 0,
  sharedList: null,
  extendedMode: false,
  trackingLocation: 'admin_portal',
  showListFilters: true,
  filtersTitle: '',
  saveFilters: () => {},
  savedFilters: {},
  isSavingFilters: false,
};
